import React from "react";
import type { SkillResult } from "../types";
import styles from "../SkillGapAssessment.module.scss";

interface KeyHighlightsPanelProps {
  skills: SkillResult[];
}

function deriveStrengths(skills: SkillResult[]): SkillResult[] {
  const exceeds = skills
    .filter((s) => s.severity === "exceeds_target")
    .sort((a, b) => b.current_score - a.current_score);

  if (exceeds.length >= 3) return exceeds.slice(0, 3);

  const meets = skills
    .filter((s) => s.severity === "meets_target")
    .sort((a, b) => b.current_score - a.current_score);

  return [...exceeds, ...meets].slice(0, 3);
}

function deriveWeaknesses(skills: SkillResult[]): SkillResult[] {
  return skills
    .filter((s) => s.gap > 0)
    .sort((a, b) => b.gap - a.gap)
    .slice(0, 3);
}

export default function KeyHighlightsPanel({ skills }: KeyHighlightsPanelProps): JSX.Element {
  const strengths = deriveStrengths(skills);
  const weaknesses = deriveWeaknesses(skills);

  return (
    <div className={styles.highlightsGrid}>
      {/* Strengths */}
      <div className={`${styles.highlightCard} ${styles.highlightCardStrength}`}>
        <h3 className={styles.highlightHeading}>
          <span className={styles.highlightIcon}>✦</span> Key Strengths
        </h3>
        <p className={styles.highlightSubtitle}>
          Top skills where you meet or exceed the target
        </p>
        {strengths.length === 0 ? (
          <p className={styles.highlightEmpty}>No skills meet or exceed the target level.</p>
        ) : (
          <div className={styles.highlightList}>
            {strengths.map((skill) => (
              <div key={skill.skill} className={styles.highlightRow}>
                <span className={styles.highlightSkillName}>{skill.skill}</span>
                <div className={styles.highlightScores}>
                  <span className={styles.scoreChip} data-color="green">
                    {skill.current_score} / {skill.target_level}
                  </span>
                  <span className={`${styles.severityBadge} ${styles[skill.severity]}`}>
                    {skill.severity === "exceeds_target" ? "Exceeds" : "Meets"} Target
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Weaknesses */}
      <div className={`${styles.highlightCard} ${styles.highlightCardWeakness}`}>
        <h3 className={styles.highlightHeading}>
          <span className={styles.highlightIcon}>▼</span> Key Weaknesses
        </h3>
        <p className={styles.highlightSubtitle}>
          Skills with the largest gap to the target level
        </p>
        {weaknesses.length === 0 ? (
          <p className={styles.highlightEmpty}>No significant gaps identified.</p>
        ) : (
          <div className={styles.highlightList}>
            {weaknesses.map((skill) => (
              <div key={skill.skill} className={styles.highlightRow}>
                <span className={styles.highlightSkillName}>{skill.skill}</span>
                <div className={styles.highlightScores}>
                  <span className={styles.scoreChip} data-color="red">
                    {skill.current_score} / {skill.target_level}
                  </span>
                  <span className={`${styles.severityBadge} ${styles[skill.severity]}`}>
                    {skill.severity.replace(/_/g, " ")}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
